<script setup lang="ts">
import { fileIcon, formatFileSize, formatUploadDate } from '@/components/documents/fileMeta'
import FinestButton from '@/components/FinestButton.vue'
import FinestCard from '@/components/FinestCard.vue'
import { useDocumentUpload } from '@/composables/useDocumentUpload'
import AttachmentController from '@controllers/AttachmentController'
import { useConfirm } from 'primevue/useconfirm'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import AttachmentData = App.Data.AttachmentData
import DocumentTypeOptionData = App.Data.Documents.DocumentTypeOptionData
import PurchaseDocumentData = App.Data.Documents.PurchaseDocumentData
import RequiredDocumentData = App.Data.Documents.RequiredDocumentData

interface HandedInFile {
  filename: string
  size: number | undefined
  icon: string
  href: string | undefined
  attachmentId: string | undefined
  bufferedType: string | undefined
  note: string
}

type RowStatus = 'stored' | 'selected' | 'open'

/**
 * Documents with a target (the borrower's proofs, a portfolio property's documents) upload
 * right away. Purchase documents wait in the browser until the property exists.
 */
interface DocumentRow {
  key: string
  acceptedTypes: DocumentTypeOptionData[]
  target: { attachableType: string; attachableId: string } | null
  handedIn: HandedInFile[]
}

const props = withDefaults(
  defineProps<{
    requirements: RequiredDocumentData[]
    purchaseRequirements: PurchaseDocumentData[]
    showMissing?: boolean
  }>(),
  { showMissing: false },
)

/** Die noch nicht hochgeladenen Dateien, nach Dokumentart. */
const files = defineModel<Record<string, File>>('files', { required: true })

const { t, locale } = useI18n()
const confirm = useConfirm()
const { uploading, upload, remove } = useDocumentUpload()

const fileInput = ref<HTMLInputElement | null>(null)
const pending = ref<{ row: DocumentRow; documentType: string } | null>(null)

const attachedFile = (document: AttachmentData): HandedInFile => {
  const uploadedOn = formatUploadDate(document.updated_at, locale.value)

  return {
    filename: document.filename ?? '',
    size: document.size,
    icon: fileIcon(document),
    href: document.id ? AttachmentController.download(document.id).url : undefined,
    attachmentId: document.id,
    bufferedType: undefined,
    note: uploadedOn ? t('file.storedOn', { date: uploadedOn }) : t('file.stored'),
  }
}

const bufferedFile = (file: File, documentType: string): HandedInFile => ({
  filename: file.name,
  size: file.size,
  icon: fileIcon({ filename: file.name, mime_type: file.type }),
  href: undefined,
  attachmentId: undefined,
  bufferedType: documentType,
  note: t('file.pending'),
})

const rows = computed<DocumentRow[]>(() => [
  ...props.requirements.map((requirement) => ({
    key: requirement.key,
    acceptedTypes: requirement.acceptedTypes,
    target: { attachableType: requirement.attachableType, attachableId: requirement.attachableId },
    handedIn: requirement.documents.map(attachedFile),
  })),
  ...props.purchaseRequirements.map((requirement) => ({
    key: requirement.key,
    acceptedTypes: [{ value: requirement.documentType, label: t(`requirements.${requirement.key}`) }],
    target: null,
    handedIn: files.value[requirement.documentType]
      ? [bufferedFile(files.value[requirement.documentType], requirement.documentType)]
      : [],
  })),
])

const isMet = (row: DocumentRow): boolean => row.handedIn.length > 0

const isMissing = (row: DocumentRow): boolean => props.showMissing && !isMet(row)

const statusOf = (row: DocumentRow): RowStatus => {
  if (!isMet(row)) {
    return 'open'
  }

  return row.target === null ? 'selected' : 'stored'
}

const statusIcon: Record<RowStatus, string> = {
  stored: 'pi pi-check-circle',
  selected: 'pi pi-upload',
  open: 'pi pi-exclamation-circle',
}

const statusClass = (row: DocumentRow): string => {
  switch (statusOf(row)) {
    case 'stored':
      return 'border-success/20 bg-success-subtle text-success'
    case 'selected':
      return 'border-action/20 bg-info-subtle text-action'
    default:
      return isMissing(row) ? 'border-warning/20 bg-warning-subtle text-warning' : 'border-line bg-surface-2 text-ink-3'
  }
}

const uploadLabel = (row: DocumentRow, type: DocumentTypeOptionData): string => {
  if (row.acceptedTypes.length > 1) {
    return type.label
  }

  return isMet(row) ? t('actions.replace') : t('actions.choose')
}

function chooseFile(row: DocumentRow, documentType: string): void {
  pending.value = { row, documentType }
  fileInput.value?.click()
}

function onSelect(event: Event): void {
  const input = event.target as HTMLInputElement
  const selected = [...(input.files ?? [])]
  const target = pending.value

  input.value = ''
  pending.value = null

  if (!target || !selected.length) {
    return
  }

  if (target.row.target === null) {
    files.value = { ...files.value, [target.documentType]: selected[0] }

    return
  }

  void upload({ ...target.row.target, documentType: target.documentType }, selected)
}

function removeFile(file: HandedInFile): void {
  if (file.bufferedType !== undefined) {
    const kept = { ...files.value }

    delete kept[file.bufferedType]
    files.value = kept

    return
  }

  confirm.require({
    header: t('delete.header'),
    message: t('delete.message', { filename: file.filename }),
    rejectProps: { label: t('delete.reject'), severity: 'secondary', outlined: true },
    acceptProps: { label: t('delete.accept'), severity: 'danger' },
    accept: () => remove(file.attachmentId!),
  })
}
</script>

<template>
  <FinestCard :title="t('title')" icon="pi pi-folder-open">
    <p class="text-ink-2 mb-5 text-sm">{{ t('intro') }}</p>

    <ul class="divide-line-2 divide-y">
      <li v-for="row in rows" :key="row.key" class="flex flex-col gap-3 py-4 first:pt-0">
        <div class="flex items-start gap-3">
          <span
            class="mt-0.5 flex size-6 flex-none items-center justify-center rounded-full"
            :class="[
              isMet(row) ? 'bg-success text-white' : 'border border-dashed',
              isMissing(row) ? 'border-warning bg-warning-subtle' : 'border-line',
            ]"
            aria-hidden="true">
            <i v-if="isMet(row)" class="pi pi-check text-[11px]!" />
          </span>

          <div class="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">
            <p class="font-medium" :class="isMissing(row) ? 'text-warning' : 'text-ink'">
              {{ t(`requirements.${row.key}`) }}
              <span class="text-danger" aria-hidden="true">*</span>
            </p>

            <span
              class="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium"
              :class="statusClass(row)">
              <i :class="statusIcon[statusOf(row)]" class="text-[11px]!" aria-hidden="true" />
              {{ t(`status.${statusOf(row)}`) }}
            </span>
          </div>
        </div>

        <ul v-if="row.handedIn.length" class="ml-9 flex flex-col gap-2">
          <li
            v-for="file in row.handedIn"
            :key="file.attachmentId ?? file.filename"
            class="border-line bg-surface-2 flex flex-wrap items-center gap-3 rounded-lg border px-3 py-2">
            <i :class="file.icon" class="text-ink-3" aria-hidden="true" />

            <div class="min-w-0 flex-1">
              <a v-if="file.href" :href="file.href" class="text-action block truncate font-medium hover:underline">
                {{ file.filename }}
              </a>
              <span v-else class="text-ink block truncate font-medium">{{ file.filename }}</span>

              <p class="text-ink-3 text-xs">{{ file.note }}</p>
            </div>

            <span class="text-ink-3 text-xs tabular-nums">{{ formatFileSize(file.size, locale) }}</span>

            <FinestButton
              variant="ghost"
              size="sm"
              icon="pi pi-trash"
              :label="t('actions.remove')"
              @click="removeFile(file)" />
          </li>
        </ul>

        <div class="ml-9 flex flex-wrap gap-2">
          <FinestButton
            v-for="type in row.acceptedTypes"
            :key="type.value"
            :variant="isMet(row) ? 'ghost' : 'secondary'"
            size="sm"
            icon="pi pi-upload"
            :label="uploadLabel(row, type)"
            :disabled="uploading"
            @click="chooseFile(row, type.value)" />
        </div>
      </li>
    </ul>

    <p v-if="purchaseRequirements.length" class="text-ink-3 mt-4 text-xs">{{ t('hint') }}</p>

    <input ref="fileInput" type="file" multiple class="hidden" @change="onSelect" />
  </FinestCard>
</template>

<i18n>
{
  "de": {
    "title": "Pflichtdokumente",
    "intro": "Diese Unterlagen brauchen wir für deine Anfrage. Was uns schon vorliegt, ist markiert – das musst du nicht noch einmal hochladen.",
    "hint": "Die Unterlagen des Ankaufsobjekts laden wir mit deiner Anfrage hoch.",
    "status": {
      "stored": "Liegt uns bereits vor",
      "selected": "Wird mit der Anfrage hochgeladen",
      "open": "Noch nicht hochgeladen"
    },
    "file": {
      "stored": "Liegt uns bereits vor – du musst sie nicht erneut hochladen.",
      "storedOn": "Bereits hochgeladen am {date} – wir haben sie schon.",
      "pending": "Wird mit deiner Anfrage hochgeladen."
    },
    "requirements": {
      "income_proof": "Gehaltsnachweise oder Einkommensteuererklärung",
      "annual_financial_statement": "Jahresabschluss des letzten Jahres",
      "business_evaluation": "BWA des aktuellen Jahres inkl. SuSa",
      "expose": "Exposé des Ankaufsobjekts",
      "building_plan": "Bauplan, Grundriss oder Wohnflächenberechnung",
      "tenant_list": "Mieterliste der Immobilie"
    },
    "actions": {
      "choose": "Datei auswählen",
      "replace": "Andere Datei hochladen",
      "remove": "Entfernen"
    },
    "delete": {
      "header": "Dokument entfernen",
      "message": "„{filename}“ wird entfernt.",
      "reject": "Abbrechen",
      "accept": "Entfernen"
    }
  },
  "en": {
    "title": "Required documents",
    "intro": "These are the documents we need for your request. Whatever we already have is marked – you do not have to upload it again.",
    "hint": "The documents of the purchased property are uploaded together with your request.",
    "status": {
      "stored": "Already with us",
      "selected": "Uploaded with the request",
      "open": "Not uploaded yet"
    },
    "file": {
      "stored": "Already with us – no need to upload it again.",
      "storedOn": "Uploaded on {date} – we already have it.",
      "pending": "Will be uploaded with your request."
    },
    "requirements": {
      "income_proof": "Payslips or income tax return",
      "annual_financial_statement": "Annual financial statement of last year",
      "business_evaluation": "Business evaluation of the current year incl. trial balance",
      "expose": "Exposé of the purchased property",
      "building_plan": "Building plan, floor plan or living area calculation",
      "tenant_list": "Tenant list of the property"
    },
    "actions": {
      "choose": "Choose file",
      "replace": "Upload a different file",
      "remove": "Remove"
    },
    "delete": {
      "header": "Remove document",
      "message": "“{filename}” will be removed.",
      "reject": "Cancel",
      "accept": "Remove"
    }
  }
}
</i18n>
